/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import * as React from 'react';
import type { GenericCallBackFunction } from '../../@types';
import ErrorState from '../ErrorState/ErrorState';
import SearchAnalytics from '../../analytics/SearchAnalytics';
import SearchHostContext from '../../utils/SearchHostContext';
import translatedStrings from '../../../translations/en.json';
type Props = {
	children: React.ReactNode;
	locale: string;
	showErrorUI?: boolean;
	onErrorCallback?: GenericCallBackFunction;
};
type State = {
	hasError: boolean;
	error: Record<string, any> | null | undefined;
	errorInfo: Record<string, any>;
};
/**
 * An error boundary implementation that renders fallback UI incase errors occur in the child component tree.
 * @augments React.Component
 */

class ErrorBoundary extends React.Component<Props, State> {
	/**
	 * Constructs a new ErrorBoundary.
	 * @param {Object} props - the props passed to the ErrorBoundary.
	 */
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
			error: null,
			errorInfo: {},
		};
	}

	/**
	 * Catches any errors in the child component tree.
	 * @param {Object} error - the error object.
	 * @param {Object} errorInfo - error info object.
	 */
	componentDidCatch(error: Record<string, any> | null | undefined, errorInfo: Record<string, any>): void {
		// Display fallback UI
		this.setState({
			hasError: true,
			error,
			errorInfo,
		});

		this.props.onErrorCallback?.({
			error,
			errorInfo,
			// this is a phony check but I have nothing better than passing name down from each component
			component: this.props.showErrorUI ? 'searchResultsPage' : 'searchBar',
		});

		const analytics = new SearchAnalytics(SearchHostContext.getContext());
		analytics.postNoResults(translatedStrings['app.search.errorstate.message.unavailable'], null, error.message);
	}

	/**
	 * Returns the error page type that should be displayed.
	 */
	getErrorType() {
		return {
			params: {
				type: 2,
			},
		};
	}

	/**
	 * Render method.
	 */
	render() {
		const ErrorStateComponent = ErrorState.component;
		if (this.state.hasError) {
			return this.props.showErrorUI ? (
				<ErrorStateComponent match={this.getErrorType()} locale={this.props.locale} />
			) : null;
		}

		return this.props.children;
	}
}

export default ErrorBoundary;
